/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
import { Buffer } from 'buffer';
import readJsonLines from 'read-json-lines-sync';

import { MimeType } from '@constants';
import { IMediaItem, instanceOfIMediaItem, IWebDirectoryItem } from '@interface/media';
import { IMetadata } from '@interface/metadata';
import { ITZIPFormat } from '@interface/tzip';
import { encode, PBNode, prepare } from '@ipld/dag-pb';
import { getTZIP21WebFormat, getTZIPFormat } from '@utils/tzip';

import { INFURA_AUTHENTICATED, INFURA_URL } from '../config';

interface IDAGItem {
  Name: string;
  Hash: string;
  Size: number;
}

const REQUEST_TRIES = 2;
const RATE_LIMIT_SLEEP = 30000;

// wrapper function that retries API call if it gets a 429 error
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function rateLimitGuard(exec: () => Promise<any>) {
  let count = 0;
  while (count < REQUEST_TRIES) {
    try {
      // try request
      const res = await exec();
      return res;
    } catch (error) {
      const text = (error as Error).message;
      if (text === 'Too Many Requests') {
        count += 1;
        if (count > REQUEST_TRIES) {
          throw error;
        }
        // retrying...
        // sleep
        await new Promise((resolve) => {
          setTimeout(resolve, RATE_LIMIT_SLEEP);
        });
      } else {
        throw error;
      }
    }
  }

  throw new Error('Request failed');
}

// pin a file or directory
export async function pinObject(hash: string, authenticated = true) {
  const url = `${INFURA_URL}/api/v0/pin/add?arg=${hash}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authenticated
      ? {
          Authorization: `Basic ${process.env.INFURA_IPFS_USER}`,
        }
      : {},
  });

  if (!res.ok) {
    throw new Error(res.statusText);
  }

  const result = await res.json();
  return result;
}

// upload single file
export async function uploadFile(file: File, pin: boolean, authenticated = true) {
  const formData = new FormData();
  formData.append('path', file);

  const url = `${INFURA_URL}/api/v0/add?pin=${pin}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authenticated
      ? {
          Authorization: `Basic ${process.env.INFURA_IPFS_USER}`,
        }
      : {},
    body: formData,
  });

  if (!res.ok) {
    throw new Error(res.statusText);
  }

  const { Hash } = await res.json();
  return Hash;
}

// upload DAG node object
async function uploadDagNode(node: PBNode, pin: boolean, authenticated = true) {
  const file = new File([encode(node)], '', {
    type: 'application/octet-stream',
  });
  const formData = new FormData();
  formData.append('data', file);

  const url = `${INFURA_URL}/api/v0/dag/put?input-codec=dag-pb&store-codec=dag-pb&pin=${pin}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authenticated
      ? {
          Authorization: `Basic ${process.env.INFURA_IPFS_USER}`,
        }
      : {},
    body: formData,
  });

  if (!res.ok) {
    throw new Error(res.statusText);
  }

  const json = await res.json();
  return json.Cid['/'];
}

// upload directory
async function uploadDirectory(files: Record<string, File>, directoryName: string, pin: boolean, authenticated = true) {
  const formData = new FormData();

  // eslint-disable-next-line no-restricted-syntax
  for (const key in files) {
    if (files[key].type !== MimeType.WEB) {
      formData.append('file', files[key], encodeURIComponent(key));
    }
  }

  const url = `${INFURA_URL}/api/v0/add?recursive=true&wrap-with-directory=true&pin=${pin}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authenticated
      ? {
          Authorization: `Basic ${process.env.INFURA_IPFS_USER}`,
        }
      : {},
    body: formData,
  });

  if (!res.ok) {
    throw new Error(res.statusText);
  }

  const data = readJsonLines(await res.text());

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rootDir = data.find((e: any) => e.Name === '');

  return {
    name: directoryName,
    hash: rootDir.Hash,
    size: parseInt(rootDir.Size, 10),
  };
}

// create and validate protobuf dag node
const createDirectoryNode = async (items: IDAGItem[]) => {
  const obj = {
    Data: '\b\x01',
    Links: items,
  };

  return prepare(obj);
};

// upload all assets as directory
export const uploadAssets = async (
  original: IMediaItem | IWebDirectoryItem,
  media: IMediaItem[],
  license: File | undefined
) => {
  const mediaFiles = media.map((e) => e.file);

  let originalHash: string;
  let originalFormat: ITZIPFormat;

  if (instanceOfIMediaItem(original)) {
    // handle individual file
    originalHash = await rateLimitGuard(() => uploadFile(original.file, true, INFURA_AUTHENTICATED));
    originalFormat = await getTZIPFormat(original.file, originalHash, original.meta);
  } else {
    // handle directory
    const { name, size, hash } = await rateLimitGuard(() =>
      uploadDirectory(original.files, 'original', true, INFURA_AUTHENTICATED)
    );
    originalHash = hash;
    originalFormat = await getTZIP21WebFormat(
      hash,
      name,
      size,
      original.meta?.width,
      original.meta?.height,
      original.meta?.aspect
    );
  }

  // upload extra media files and generate formats
  const mediaHashes: string[] = [];
  for (let i = 0; i < media.length; i += 1) {
    const hash = await rateLimitGuard(() => uploadFile(media[i].file, true, INFURA_AUTHENTICATED));
    mediaHashes.push(hash);
  }

  const mediaFormats = [];
  for (let i = 0; i < media.length; i += 1) {
    const { file, meta } = media[i];
    const format = await getTZIPFormat(file, mediaHashes[i], meta);
    mediaFormats.push(format);
  }

  // create media directory for extra media
  const mediaItems: IDAGItem[] = mediaFiles.map((file, index) => {
    return {
      Name: file.name,
      Hash: mediaHashes[index],
      Size: file.size,
    };
  });

  const mediaNode = await createDirectoryNode(mediaItems);
  const mediaCid = await rateLimitGuard(() => uploadDagNode(mediaNode, true, INFURA_AUTHENTICATED));
  const mediaCumulativeSize = mediaFiles.reduce((total, file) => total + file.size, 0);

  // create root directory for orignal file and extra media
  const rootItems: IDAGItem[] = [
    {
      Name: originalFormat.fileName,
      Hash: originalHash,
      Size: originalFormat.fileSize,
    },
    {
      Name: 'media',
      Hash: mediaCid,
      Size: mediaCumulativeSize,
    },
  ];

  // add license file if applicable
  let licenseHash;
  if (license) {
    licenseHash = await rateLimitGuard(() => uploadFile(license, true, INFURA_AUTHENTICATED));
    rootItems.push({
      Name: license.name,
      Hash: licenseHash,
      Size: license.size,
    });
  }

  const rootNode = await createDirectoryNode(rootItems);
  const rootCid = await rateLimitGuard(() => uploadDagNode(rootNode, true, INFURA_AUTHENTICATED));

  // await pinObject(rootCid);

  return {
    pinHash: rootCid.toString(),
    licenseHash,
    formats: [originalFormat, ...mediaFormats],
  };
};

export const uploadMetadata = async ({
  name,
  description,
  creators,
  type,
  tags,
  rights,
  rightUri,
  artifactUri,
  displayUri,
  thumbnailUri,
  pinUri,
  formats,
  accessibility,
}: IMetadata) => {
  const buffer: Buffer = Buffer.from(
    JSON.stringify({
      decimals: 0,
      shouldPreferSymbol: false,
      isBooleanAmount: false,
      symbol: 'ITEM',
      name,
      description,
      creators,
      type,
      tags,
      language: 'en',
      rights,
      ...(rightUri && { rightUri }),
      artifactUri,
      displayUri,
      thumbnailUri,
      pinUri,
      formats,
      accessibility,
    })
  );

  const file = new File([buffer], 'metadata.json', {
    type: 'application/json',
  });

  const hash = await rateLimitGuard(() => uploadFile(file, true, INFURA_AUTHENTICATED));

  return hash;
};
